import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { FlashMapNodeModel } from '../models/node.model';
import {
  Direction,
  EventData,
  EventType,
  FlashMapEventCallback,
  FlashMapLayoutMode,
  FlashMapNodeLineStyle,
  FlashMapOptions,
  MindMapData,
  NodeImageMeta,
} from '../models/types';
import { JsMindAdapter } from '../engines/jsmind-adapter';
import { MindMapEngine } from '../engines/mind-map-engine';

@Injectable()
export class FlashMapService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly engine: MindMapEngine;

  readonly initialized = signal(false);
  readonly selectedNode = signal<FlashMapNodeModel | null>(null);
  readonly editable = signal(false);
  readonly zoomLevel = signal(1);
  readonly viewport = signal({ zoom: 1, pan: { x: 0, y: 0 } });

  readonly isReady = computed(() => this.initialized() && this.engine.isReady());

  private eventCallbacks: FlashMapEventCallback[] = [];

  constructor() {
    this.engine = new JsMindAdapter(this.platformId);
  }

  async init(options: FlashMapOptions): Promise<void> {
    try {
      await this.engine.init(options);
      if (!this.engine.isReady()) {
        return;
      }
      this.initialized.set(true);
      this.editable.set(options.editable ?? false);
      this.syncZoomLevel();
      this.syncViewport();

      this.engine.addEventListener((type: number, data: EventData) => {
        this.handleEvent(type, data);
      });
    } catch (error) {
      console.error('Failed to initialize FlashMapService:', error);
    }
  }

  show(mindData: MindMapData | null, skipCentering = false): void {
    if (!this.engine.isReady()) {
      console.error('FlashMapService not initialized. Call init() first.');
      return;
    }
    this.engine.show(mindData, skipCentering);
    this.selectedNode.set(null);
    this.syncZoomLevel();
    this.syncViewport();
  }

  setLayoutMode(layoutMode: FlashMapLayoutMode): void {
    if (!this.engine.isReady()) {
      return;
    }
    this.engine.setLayoutMode(layoutMode);
    this.syncViewport();
  }

  getMeta(): { name: string; author: string; version: string } {
    return this.engine.getMeta();
  }

  getData(format: 'node_tree' | 'node_array' | 'freemind' = 'node_tree'): object {
    return this.engine.getData(format);
  }

  shootScreenshot(): void {
    this.engine.shootScreenshot();
  }

  getRoot(): FlashMapNodeModel | null {
    return this.engine.getRoot();
  }

  getNode(nodeId: string): FlashMapNodeModel | null {
    return this.engine.getNode(nodeId);
  }

  addNode(
    parentNode: string | FlashMapNodeModel,
    nodeId: string,
    topic: string,
    data?: Record<string, unknown>,
    direction?: Direction | string | number,
  ): FlashMapNodeModel | null {
    return this.engine.addNode(parentNode, nodeId, topic, data, direction);
  }

  addNodes(
    parentNode: string | FlashMapNodeModel,
    nodesData: Array<{
      id: string;
      topic: string;
      data?: Record<string, unknown>;
      direction?: Direction | string | number;
      children?: Array<unknown>;
    }>,
  ): Array<FlashMapNodeModel | null> {
    return this.engine.addNodes(parentNode, nodesData);
  }

  removeNode(node: string | FlashMapNodeModel): boolean {
    return this.engine.removeNode(node);
  }

  moveNode(
    nodeId: string,
    beforeId: string | null,
    parentId: string,
    direction?: Direction | string | number,
  ): FlashMapNodeModel | null {
    return this.engine.moveNode(nodeId, beforeId, parentId, direction);
  }

  updateNode(nodeId: string, topic: string): void {
    this.engine.updateNode(nodeId, topic);
  }

  refreshNode(nodeId: string): void {
    this.engine.refreshNode(nodeId);
  }

  setNodeImage(nodeId: string, image: NodeImageMeta | null): void {
    this.engine.setNodeImage(nodeId, image);
  }

  setNodeImagePosition(nodeId: string, position: 'top' | 'bottom' | 'left' | 'right'): void {
    this.engine.setNodeImagePosition(nodeId, position);
  }

  selectNode(node: string | FlashMapNodeModel): void {
    this.engine.selectNode(node);
    this.selectedNode.set(this.engine.getSelectedNode());
  }

  getSelectedNode(): FlashMapNodeModel | null {
    return this.engine.getSelectedNode();
  }

  selectClear(): void {
    this.engine.selectClear();
    this.selectedNode.set(null);
  }

  expandNode(node: string | FlashMapNodeModel): void {
    this.engine.expandNode(node);
  }

  collapseNode(node: string | FlashMapNodeModel): void {
    this.engine.collapseNode(node);
  }

  toggleNode(node: string | FlashMapNodeModel): void {
    this.engine.toggleNode(node);
  }

  expandAll(): void {
    this.engine.expandAll();
  }

  collapseAll(): void {
    this.engine.collapseAll();
  }

  expandToDepth(depth: number): void {
    this.engine.expandToDepth(depth);
  }

  setNodeColor(nodeId: string, bgColor?: string, fgColor?: string): void {
    this.engine.setNodeColor(nodeId, bgColor, fgColor);
  }

  setNodeStyleColor(
    nodeId: string,
    key: 'background-color' | 'foreground-color' | 'border-color',
    color?: string | null,
  ): void {
    this.engine.setNodeStyleColor(nodeId, key, color);
  }

  setNodeDescendantLineColor(nodeId: string, color: string): void {
    this.engine.setNodeDescendantLineColor(nodeId, color);
  }

  resetNodeDescendantLineColorsToTheme(nodeId: string): void {
    this.engine.resetNodeDescendantLineColorsToTheme(nodeId);
  }

  setNodeDescendantLineStyle(nodeId: string, style: FlashMapNodeLineStyle): void {
    this.engine.setNodeDescendantLineStyle(nodeId, style);
  }

  setNodeStyleValue(
    nodeId: string,
    key: 'border-width' | 'border-style' | 'font-family',
    value?: string | null,
  ): void {
    this.engine.setNodeStyleValue(nodeId, key, value);
  }

  setNodeShape(nodeId: string, shape?: string | null): void {
    this.engine.setNodeShape(nodeId, shape);
  }

  resetNodeShapeToTheme(nodeId: string): void {
    this.engine.resetNodeShapeToTheme(nodeId);
  }

  setNodeLineShape(nodeId: string, shape?: 'underline' | null): void {
    this.engine.setNodeLineShape(nodeId, shape);
  }

  setNodeFontStyle(
    nodeId: string,
    size?: number | null,
    weight?: string | null,
    style?: string | null,
  ): void {
    this.engine.setNodeFontStyle(nodeId, size, weight, style);
  }

  clearNodeCustomStyles(): void {
    this.engine.clearNodeCustomStyles();
  }

  setTheme(theme: string | null): void {
    this.engine.setTheme(theme);
  }

  enableEdit(): void {
    this.engine.enableEdit();
    this.editable.set(true);
  }

  disableEdit(): void {
    this.engine.disableEdit();
    this.editable.set(false);
  }

  getEditable(): boolean {
    return this.engine.getEditable();
  }

  beginEdit(node: string | FlashMapNodeModel): void {
    this.engine.beginEdit(node);
  }

  endEdit(): void {
    this.engine.endEdit();
  }

  resize(): void {
    this.engine.resize();
  }

  zoomIn(): void {
    this.engine.zoomIn();
    this.syncZoomLevel();
  }

  zoomOut(): void {
    this.engine.zoomOut();
    this.syncZoomLevel();
  }

  resetZoom(): void {
    this.engine.resetZoom();
    this.syncZoomLevel();
  }

  setZoom(zoomLevel: number): void {
    this.engine.setZoom(zoomLevel);
    this.syncZoomLevel();
  }

  getZoom(): number {
    return this.engine.getZoom();
  }

  getPan(): { x: number; y: number } {
    return this.engine.getPan();
  }

  setPan(x: number, y: number): void {
    this.engine.setPan(x, y);
  }

  getContainer(): HTMLElement | null {
    return this.engine.getContainer();
  }

  centerNode(node?: string | FlashMapNodeModel): void {
    this.engine.centerNode(node);
  }

  addEventListener(callback: FlashMapEventCallback): void {
    this.eventCallbacks.push(callback);
  }

  clearEventListener(): void {
    this.eventCallbacks = [];
    this.engine.clearEventListener();
  }

  private handleEvent(type: number, data: EventData): void {
    if (type === EventType.SELECT) {
      const node = this.getSelectedNode();
      this.selectedNode.set(node);
    }
    if (type === EventType.RESIZE) {
      this.syncZoomLevel();
    }
    if (type === EventType.VIEWPORT) {
      this.syncViewport();
    }

    this.eventCallbacks.forEach((callback) => callback(type as EventType, data));
  }

  destroy(): void {
    this.clearEventListener();
    this.engine.destroy();
    this.initialized.set(false);
    this.selectedNode.set(null);
    this.zoomLevel.set(1);
    this.viewport.set({ zoom: 1, pan: { x: 0, y: 0 } });
  }

  private syncZoomLevel(): void {
    this.zoomLevel.set(this.engine.getZoom());
  }

  private syncViewport(): void {
    this.viewport.set({
      zoom: this.engine.getZoom(),
      pan: this.engine.getPan(),
    });
  }
}
